import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ArrowRight, ChevronDown } from 'lucide-react';
import { Wordmark } from './Logo';
import USPBar from './USPBar';
import { useLang } from '../LangContext';
import { translations, company } from '../i18n';

const telHref = (p) => `tel:${(p || '').replace(/\s/g, '')}`;
const navTestId = (to) => String(to || 'nav').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'home';

// Paired localized legal routes — when the user toggles language while
// standing on one of these, we navigate to the sibling URL so the
// content stays in sync with the chosen language.
const LEGAL_PAIRS = {
  '/datenschutz': { nl: '/privacy' },
  '/privacy':     { de: '/datenschutz' },
  '/agb':         { nl: '/voorwaarden' },
  '/voorwaarden': { de: '/agb' },
  '/impressum':   { nl: '/colofon' },
  '/colofon':     { de: '/impressum' },
};

function DesktopSimpleDropdown({ items = [], open = false, onNavigate = () => {} }) {
  return (
    <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-5 transition-opacity ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
      <div className="w-[300px] rounded-2xl bg-white border border-black/5 shadow-[0_22px_60px_-28px_rgba(31,29,26,0.45)] p-2">
        {items.map((child) => (
          <NavLink
            key={child.id || child.to}
            to={child.to}
            onClick={onNavigate}
            className="block px-3 py-2 rounded-xl text-[13px] text-[#1F1D1A]/80 hover:bg-[#FAF8F2] hover:text-[#F26B1A]"
          >
            {child.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function DesktopMegaMenu({ link, open = false, onNavigate = () => {} }) {
  const groups = link.children || [];
  return (
    <div className={`fixed left-1/2 top-[76px] xl:top-[84px] w-[min(1440px,calc(100vw-48px))] -translate-x-1/2 pt-4 transition-all duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
      <div className="max-h-[calc(100dvh-112px)] overflow-y-auto overscroll-contain rounded-[24px] bg-[#FAF8F2] border border-black/[0.06] shadow-[0_34px_90px_-42px_rgba(31,29,26,0.55)]">
        <div className={`grid ${link.featuredImage ? 'xl:grid-cols-[minmax(0,1fr)_280px]' : 'grid-cols-1'}`}>
          <div className="p-5 lg:p-6">
            <div className="flex items-center justify-between gap-4 border-b border-[#E5E0D2] pb-4">
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-semibold">Megamenue</p>
                <p className="mt-1 font-display text-[24px] text-[#1F1D1A] leading-none">{link.label}</p>
              </div>
              <NavLink to={link.to} onClick={onNavigate} className="hidden sm:inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#5C5852] hover:text-[#F26B1A]">
                Zur Kategorie <ArrowRight className="w-3.5 h-3.5" />
              </NavLink>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-7 gap-y-5">
              {groups.map((group) => (
                <div key={group.id || group.to} className="min-w-0">
                  <NavLink
                    to={group.to}
                    onClick={onNavigate}
                    className="inline-flex items-center gap-1.5 font-display text-[18px] leading-tight text-[#1F1D1A] hover:text-[#F26B1A]"
                  >
                    {group.label}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </NavLink>
                  {group.children?.length > 0 ? (
                    <div className="mt-2.5 space-y-2">
                      {group.children.map((child) => (
                        <NavLink
                          key={child.id || child.to}
                          to={child.to}
                          onClick={onNavigate}
                          className="block text-[13px] leading-snug text-[#2F2B24] font-medium hover:text-[#F26B1A]"
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : group.description ? (
                    <p className="mt-2 text-[13px] leading-relaxed text-[#5C5852]">{group.description}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          {link.featuredImage && (
            <NavLink to={link.featuredTo || link.to} onClick={onNavigate} className="hidden xl:block relative min-h-[320px] overflow-hidden bg-[#1F1D1A] group/card">
              <img
                src={link.featuredImage}
                alt=""
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                {link.featuredEyebrow && (
                  <p className="text-[10.5px] uppercase tracking-[0.22em] text-white/75 font-semibold">{link.featuredEyebrow}</p>
                )}
                <p className="mt-2 font-display text-[26px] leading-tight">{link.featuredLabel || link.label}</p>
              </div>
            </NavLink>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * NavBar — global navigation for all pages.
 *
 * Replaces the old hash-anchor only nav with real React Router NavLinks
 * pointing to category and utility pages. Top of every page.
 */
export default function NavBar() {
  const { lang, setLang, t, overlay, contentReady } = useLang();
  const [open, setOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const phone = lang === 'de' ? company.phoneDE : company.phoneNL;

  // Language-toggle helper. On localized legal routes we ALWAYS navigate
  // to the paired URL (even if the global context lang already matches),
  // because the URL is the source of truth there. Elsewhere we just
  // flip the global context.
  const switchLang = (next) => {
    const pair = LEGAL_PAIRS[location.pathname];
    if (pair && pair[next]) {
      setLang(next);
      navigate(pair[next]);
      return;
    }
    if (next !== lang) setLang(next);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenus = () => {
    setOpen(false);
    setDesktopOpen(null);
  };

  // Close menus on route change
  useEffect(() => { closeMenus(); }, [location.pathname]);

  const labels = lang === 'de'
    ? { ramen: 'Fenster', deuren: 'Türen', lichtschachten: 'Lichtschächte', stalen: 'Muster', config: 'Konfigurator', contact: 'Kontakt' }
    : { ramen: 'Ramen', deuren: 'Deuren', lichtschachten: 'Lichtschachten', stalen: 'Stalen', config: 'Configurator', contact: 'Contact' };

  const fallbackNavLinks = [
    { to: '/ramen', label: labels.ramen },
    { to: '/deuren', label: labels.deuren },
    { to: '/lichtschachten', label: labels.lichtschachten },
    { to: '/configurator', label: labels.config },
    { to: '/gratis-stalen', label: labels.stalen },
    { to: '/contact', label: labels.contact },
  ];
  const labelFor = (item) => {
    if (typeof item?.label === 'string') return item.label;
    return item?.label?.[lang] || item?.label?.nl || item?.label?.de || item?.title || item?.path || '';
  };
  const localizedText = (value) => {
    if (typeof value === 'string') return value;
    return value?.[lang] || value?.nl || value?.de || '';
  };
  const normalizeNavItem = (item) => ({
    id: item.id,
    to: item.path || item.to || '#',
    label: labelFor(item),
    description: localizedText(item.description),
    featuredImage: item.featuredImage || item.image || '',
    featuredTo: item.featuredTo || item.path || item.to || '#',
    featuredLabel: localizedText(item.featuredLabel),
    featuredEyebrow: localizedText(item.featuredEyebrow),
    children: Array.isArray(item.children)
      ? item.children.filter((child) => child && child.hidden !== true).map(normalizeNavItem)
      : [],
  });
  const customNavLinks = Array.isArray(overlay?.navigation)
    ? overlay.navigation
        .filter((item) => item && item.hidden !== true)
        .map(normalizeNavItem)
        .filter((item) => item.label && item.to)
    : [];
  const navReady = contentReady || customNavLinks.length > 0;
  const navLinks = customNavLinks.length ? customNavLinks : (navReady ? fallbackNavLinks : []);

  return (
    <div className="fixed top-0 inset-x-0 z-50">
      <USPBar items={t.uspBar} />
      <header data-testid="main-nav" className="px-2 sm:px-0 pt-3 sm:pt-4">
        <div
          className={`max-w-[1440px] mx-auto h-[56px] sm:h-[64px] flex items-center justify-between rounded-full pl-4 lg:pl-10 pr-2 sm:pr-2.5 transition-all duration-500 ${
            scrolled
              ? 'bg-[#FAF8F2]/95 backdrop-blur-xl shadow-[0_18px_40px_-22px_rgba(31,29,26,0.30)] border border-black/[0.05]'
              : 'bg-white shadow-[0_18px_40px_-22px_rgba(31,29,26,0.18)] border border-black/[0.05]'
          }`}
        >
          <Link to="/" data-testid="nav-logo" className="text-[#1F1D1A] inline-flex items-center gap-2 shrink-0">
            <span className="sm:hidden"><Wordmark size={16} showTagline={false} testId="wordmark-nav-mobile" /></span>
            <span className="hidden sm:inline-block"><Wordmark size={22} testId="wordmark-nav" /></span>
          </Link>

          <nav className={`hidden lg:flex items-center gap-6 xl:gap-8 transition-opacity duration-150 ${navReady ? 'opacity-100' : 'opacity-0'}`}>
            {navLinks.map((l) => {
              const hasChildren = l.children?.length > 0;
              const hasGroups = hasChildren && l.children.some((child) => child.children?.length > 0);
              return (
                <div
                  key={l.to}
                  className="relative py-6 -my-6"
                  onMouseEnter={() => setDesktopOpen(l.to)}
                  onMouseLeave={() => setDesktopOpen(null)}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${navTestId(l.to)}`}
                    onClick={closeMenus}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 text-[13px] font-medium tracking-wide transition-colors ${
                        isActive ? 'text-[#F26B1A]' : 'text-[#1F1D1A]/80 hover:text-[#1F1D1A]'
                      }`
                    }
                  >
                    {l.label}
                    {hasChildren && <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${desktopOpen === l.to ? 'rotate-180' : ''}`} />}
                  </NavLink>
                  {hasChildren && (
                    hasGroups
                      ? <DesktopMegaMenu link={l} open={desktopOpen === l.to} onNavigate={closeMenus} />
                      : <DesktopSimpleDropdown items={l.children} open={desktopOpen === l.to} onNavigate={closeMenus} />
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <a href={telHref(phone)} data-testid="nav-phone-badge"
              className="hidden sm:inline-flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full text-[12.5px] font-medium text-[#1F1D1A] bg-[#F26B1A]/10 hover:bg-[#F26B1A]/20 transition-colors border border-[#F26B1A]/30">
              <Phone className="w-3.5 h-3.5 text-[#F26B1A]" />
              <span className="hidden md:inline">{phone}</span>
            </a>
            <div data-testid="language-switcher" className="flex items-center text-[11px] sm:text-[11.5px] font-medium text-[#1F1D1A]/70 px-0.5 sm:px-1">
              <button onClick={() => switchLang('nl')} data-testid="lang-nl" className={`px-1 ${lang === 'nl' ? 'text-[#1F1D1A]' : ''}`}>NL</button>
              <span className="px-1 opacity-30">/</span>
              <button onClick={() => switchLang('de')} data-testid="lang-de" className={`px-1 ${lang === 'de' ? 'text-[#1F1D1A]' : ''}`}>DE</button>
            </div>
            <Link to="/offerte" data-testid="nav-cta-quote"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-medium bg-[#F26B1A] text-white hover:bg-[#D85A0A] transition-colors">
              {t.nav.cta}
            </Link>
            <a href={telHref(phone)} data-testid="nav-phone-mobile" aria-label="call"
              className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F26B1A] text-white">
              <Phone className="w-3.5 h-3.5" />
            </a>
            <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle"
              className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 inline-flex items-center justify-center rounded-full border border-black/15 bg-white/90 text-[#1F1D1A]"
              aria-label="menu">
              {open ? <X className="w-4 h-4 sm:w-5 sm:h-5" /> : <Menu className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" data-lenis-prevent className="mobile-menu-panel lg:hidden mt-2 max-w-[1440px] mx-auto rounded-2xl bg-white border border-black/5 shadow-[0_18px_40px_-22px_rgba(31,29,26,0.30)] overflow-y-auto touch-pan-y">
            <nav className="mobile-menu-content px-6 pt-6 flex flex-col gap-5">
              {navLinks.map((l) => (
                <div key={l.to} className="space-y-2">
                  <NavLink
                    to={l.to}
                    onClick={closeMenus}
                    data-testid={`mobile-nav-${l.to.replace('/', '')}`}
                    className={({ isActive }) =>
                      `font-display text-[26px] ${isActive ? 'text-[#F26B1A]' : 'text-[#1F1D1A]'}`
                    }
                  >
                    {l.label}
                  </NavLink>
                  {l.children?.length > 0 && (
                    <div className="pl-4 border-l border-[#E5E0D2] space-y-2">
                      {l.children.map((child) => (
                        <div key={child.id || child.to} className="space-y-1.5">
                          <NavLink to={child.to} onClick={closeMenus} className="block text-[14px] font-semibold text-[#1F1D1A]">
                            {child.label}
                          </NavLink>
                          {child.children?.length > 0 && (
                            <div className="pl-3 space-y-1.5">
                              {child.children.map((sub) => (
                                <NavLink key={sub.id || sub.to} to={sub.to} onClick={closeMenus} className="block text-[13px] text-[#5C5852]">
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a href={telHref(phone)} className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#F26B1A]/10 text-[#1F1D1A] border border-[#F26B1A]/30">
                <Phone className="w-4 h-4 text-[#F26B1A]" /> {phone}
              </a>
              <Link to="/offerte" onClick={closeMenus} className="mt-2 pill pill-leaf justify-center">
                {t.nav.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </nav>
          </div>
        )}
      </header>
    </div>
  );
}
